/** Forma de una nota sintetizada. */
interface Tone {
  readonly frequency: number;
  readonly durationMs: number;
  readonly type: OscillatorType;
}

const MASTER_VOLUME = 0.18;

/**
 * Servicio de sonido basado en la Web Audio API. Genera los efectos por codigo
 * (osciladores) en lugar de cargar archivos de audio, lo que mantiene el juego
 * liviano y sin dependencias de assets. Los navegadores exigen un gesto del usuario
 * para reproducir audio, por eso el contexto se reanuda al primer uso.
 */
export class AudioService {
  private context: AudioContext | null = null;
  private muted = false;

  public get isMuted(): boolean {
    return this.muted;
  }

  public toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  public playClick(): void {
    this.playSequence([{ frequency: 520, durationMs: 70, type: 'triangle' }]);
  }

  public playHit(): void {
    this.playSequence([{ frequency: 660, durationMs: 80, type: 'sine' }]);
  }

  public playMiss(): void {
    this.playSequence([{ frequency: 180, durationMs: 140, type: 'sawtooth' }]);
  }

  public playCorrect(): void {
    this.playSequence([
      { frequency: 660, durationMs: 90, type: 'sine' },
      { frequency: 880, durationMs: 120, type: 'sine' },
    ]);
  }

  public playWrong(): void {
    this.playSequence([
      { frequency: 220, durationMs: 120, type: 'square' },
      { frequency: 160, durationMs: 160, type: 'square' },
    ]);
  }

  public playReward(): void {
    this.playSequence([
      { frequency: 523, durationMs: 90, type: 'triangle' },
      { frequency: 659, durationMs: 90, type: 'triangle' },
      { frequency: 784, durationMs: 140, type: 'triangle' },
    ]);
  }

  private ensureContext(): AudioContext | null {
    if (this.context === null) {
      try {
        this.context = new AudioContext();
      } catch (error) {
        console.error('Web Audio API no disponible en este navegador.', error);
        return null;
      }
    }
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    return this.context;
  }

  private playSequence(tones: Tone[]): void {
    if (this.muted) {
      return;
    }
    const context = this.ensureContext();
    if (context === null) {
      return;
    }

    let startTime = context.currentTime;
    for (const tone of tones) {
      this.scheduleTone(context, tone, startTime);
      startTime += tone.durationMs / 1000;
    }
  }

  private scheduleTone(context: AudioContext, tone: Tone, startTime: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const endTime = startTime + tone.durationMs / 1000;

    oscillator.type = tone.type;
    oscillator.frequency.setValueAtTime(tone.frequency, startTime);

    gain.gain.setValueAtTime(MASTER_VOLUME, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(startTime);
    oscillator.stop(endTime);
  }
}
